import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { SearchField } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import { dataMergingAnalytics } from "@app/scene/settings/settingsAnaytics";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import { getEntityType, getSearchText, isDismissedShowed } from "../../store";
import { hideDismissed, setSearchText, showDismissed } from "../../store/actions";

import DuplicateCriteriaModal from "./DuplicateCriteriaModal";

interface Props {
  dismissedShowed: boolean;
  entityType: EntityTypeSupportingDataMerging;
  onHideDismissed: typeof hideDismissed;
  onSearchTextChange: typeof setSearchText;
  onShowDismissed: typeof showDismissed;
  searchText: string;
}

const ToolBar: React.FC<Props> = ({
  dismissedShowed,
  entityType,
  onHideDismissed,
  onSearchTextChange,
  onShowDismissed,
  searchText,
}) => {
  const intl = useIntl();
  const [search, setSearch] = useStateWithDebouncedListener([onSearchTextChange, 500], "");
  const [duplicateModalVisible, showDuplicateModal, hideDuplicateModal] = useBoolean();

  const handleHideDismissed = useCallback(() => {
    dataMergingAnalytics?.clicked([
      getEntityTypeAnalyticsName(entityType, false, true),
      "Hide dismissed duplicates",
    ]);
    onHideDismissed();
  }, [entityType, onHideDismissed]);

  const handleShowDismissed = useCallback(() => {
    dataMergingAnalytics?.clicked([
      getEntityTypeAnalyticsName(entityType, false, true),
      "Review dismissed duplicates",
    ]);
    onShowDismissed();
  }, [entityType, onShowDismissed]);

  useEffect(() => {
    setSearch(searchText);
  }, [setSearch, searchText]);

  return (
    <Row align="middle" gutter={layout.spacerS} justify="end">
      <Col>
        <SearchField
          onChange={setSearch}
          placeholder={intl.formatMessage({
            id: "settings.mergeDuplicates.search.placeholder",
            defaultMessage: "Search duplicates",
            description: "Merge duplicates - search input - placeholder",
          })}
          size="middle"
          value={search}
        />
      </Col>
      <Col>
        <Dropdown
          menu={{
            items: [
              ...(dismissedShowed
                ? [
                    {
                      key: "hideDismissed",
                      label: intl.formatMessage({
                        id: "settings.mergeDuplicates.hideDismissed",
                        defaultMessage: "Back to standard view",
                        description: "Merge duplicates - Back to standard view",
                      }),
                      onClick: handleHideDismissed,
                    },
                  ]
                : [
                    {
                      key: "showDismissed",
                      label: intl.formatMessage({
                        id: "settings.mergeDuplicates.showDismissed",
                        defaultMessage: "Review dismissed duplicates",
                        description: "Merge duplicates - Back to standard view",
                      }),
                      onClick: handleShowDismissed,
                    },
                  ]),
              {
                key: "setDuplicateCriteria",
                label: intl.formatMessage({
                  id: "settings.mergeDuplicates.setDuplicateCriteria",
                  defaultMessage: "Set duplicate criteria",
                  description: "Merge duplicates - Back to standard view",
                }),
                onClick: showDuplicateModal,
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreButton horizontal />
        </Dropdown>
      </Col>
      {duplicateModalVisible && (
        <DuplicateCriteriaModal defaultEntityType={entityType} onHide={hideDuplicateModal} />
      )}
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  dismissedShowed: isDismissedShowed(state),
  entityType: getEntityType(state),
  searchText: getSearchText(state),
});

const mapDispatchToProps = {
  onHideDismissed: hideDismissed,
  onSearchTextChange: setSearchText,
  onShowDismissed: showDismissed,
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolBar);
